import React, { useState } from 'react';
import { contactData, companyInfo } from '../data/mock';
import { useContactContent } from '../hooks/useCMSContent';
import { toast } from 'sonner';
import { Mail, Phone, ArrowRight, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { trackContactSubmission } from '../utils/analytics';

const ContactSection = () => {
  const [headerRef, headerVisible] = useScrollAnimation(0.2);
  const [formRef, formVisible] = useScrollAnimation(0.1);
  
  // Use CMS content with fallback to mock data
  const { contact: cmsContact } = useContactContent();
  const contact = cmsContact || {};
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          company: formData.company,
          message: formData.message
        })
      });
      
      if (response.ok) {
        trackContactSubmission();
        toast.success('Message sent successfully! We\'ll get back to you within 24 hours.');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Use CMS values or fallback to defaults
  const displayEmail = contact.email || companyInfo.email;
  const displayPhone = contact.phone || companyInfo.phone;
  const displayAddress = contact.address || companyInfo.fullAddress;
  const displayTitle = contact.form_title || contactData.title;
  const displaySubtitle = contact.form_subtitle || contactData.subtitle;

  return (
    <section id="contact" className="py-20 bg-[#0A0A0A] overflow-hidden">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left Side - Info */}
          <motion.div
            ref={headerRef}
            initial={{ opacity: 0, x: -40 }}
            animate={headerVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
          >
            <motion.span
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FF6B00]/10 border border-[#FF6B00]/30 text-[10px] font-mono-tech uppercase tracking-[0.25em] text-[#FF6B00] mb-6"
            >
              Contact Us
            </motion.span>
            <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight mb-6">
              {displayTitle}
            </h2>
            <p className="text-[#A1A1AA] text-lg mb-10 leading-relaxed">
              {displaySubtitle}
            </p>

            {/* Contact Info - Icons only */}
            <div className="flex flex-wrap gap-4 mt-2">
              <motion.a
                href={`mailto:${displayEmail}`}
                initial={{ opacity: 0, y: 20 }}
                animate={headerVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.3 }}
                whileHover={{ scale: 1.05 }}
                className="flex items-center gap-3 px-5 py-3 bg-white/[0.03] rounded-xl hover:bg-white/[0.05] transition-colors"
              >
                <Mail className="w-5 h-5 text-white" />
                <span className="text-sm text-[#A1A1AA]">{displayEmail}</span>
              </motion.a>

              <motion.a
                href={`tel:${displayPhone}`}
                initial={{ opacity: 0, y: 20 }}
                animate={headerVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.4 }}
                whileHover={{ scale: 1.05 }}
                className="flex items-center gap-3 px-5 py-3 bg-white/[0.03] rounded-xl hover:bg-white/[0.05] transition-colors"
              >
                <Phone className="w-5 h-5 text-white" />
                <span className="text-sm text-[#A1A1AA]">{displayPhone}</span>
              </motion.a>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={headerVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.5 }}
                className="flex items-center gap-3 px-5 py-3 bg-white/[0.03] rounded-xl"
              >
                <MapPin className="w-5 h-5 text-white flex-shrink-0" />
                <span className="text-sm text-[#A1A1AA]">{displayAddress}</span>
              </motion.div>
            </div>
          </motion.div>

          {/* Right Side - Form */}
          <motion.div
            ref={formRef}
            initial={{ opacity: 0, x: 40 }}
            animate={formVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="bg-white/[0.03] rounded-3xl p-8 md:p-10"
          >
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    First name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('firstName')}
                    onBlur={() => setFocusedField(null)}
                    required
                    className="w-full px-5 py-4 bg-[#0A0A0A] border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#333]/10 focus:border-[#333] transition-all duration-300 card-glow-hover"
                    placeholder="John"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Last name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="w-full px-5 py-4 bg-[#0A0A0A] border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#333]/10 focus:border-[#333] transition-all duration-300 card-glow-hover"
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Work email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-5 py-4 bg-[#0A0A0A] border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#333]/10 focus:border-[#333] transition-all duration-300 card-glow-hover"
                  placeholder="john@company.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Company
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-5 py-4 bg-[#0A0A0A] border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#333]/10 focus:border-[#333] transition-all duration-300 card-glow-hover"
                  placeholder="Your company name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Tell us about your project
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-5 py-4 bg-[#0A0A0A] border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#333]/10 focus:border-[#333] transition-all duration-300 resize-none card-glow-hover"
                  placeholder="What would you like to automate or build?"
                />
              </div>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="group relative w-full py-4 bg-[#FF6B00] text-white rounded-xl hover:bg-[#FF8C3B] shadow-lg shadow-orange-500/30 hover:shadow-[0_0_60px_rgba(255,107,0,0.45)] transition-all duration-500 font-semibold disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3 overflow-hidden"
                data-testid="contact-submit-btn"
              >
                {/* Tracing beam over button surface on hover */}
                <span className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-[1100ms] ease-out" />
                <span className="relative z-10 flex items-center gap-3">
                  {isSubmitting ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                    />
                  ) : (
                    <>
                      Send Message
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                    </>
                  )}
                </span>
              </motion.button>

              <p className="text-xs text-[#71717A] text-center">
                We typically respond within 24 hours.
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
